import { useEffect, useState } from 'react';
import FilterSidebar from '../components/FilterSidebar';
import InfluencerCard from '../components/InfluencerCard';
import SEO from '../components/SEO';

type Creator = {
  id: string;
  full_name?: string | null;
  brand_name?: string | null;
  profile_photo_url?: string | null;
  country?: string | null;
  city?: string | null;
  primary_niche?: string | null;
  secondary_niches?: string | null;
  total_followers?: number | null;
  avg_engagement_rate?: number | null;
  languages?: string | null;
};

type CreatorsResponse = {
  success: boolean;
  creators: Creator[];
  countries: string[];
  total: number;
  hasMore?: boolean;
};

export default function TopInfluencersPage() {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState('');
  const [nicheFilter, setNicheFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const limit = 12;

  useEffect(() => {
    const loadCreators = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (countryFilter) {
          params.set('country', countryFilter);
        }
        params.set('limit', String(limit));
        params.set('offset', String((currentPage - 1) * limit));

        console.log('🟢 [TopInfluencers] Fetching:', `/api/creators?${params}`);

        const response = await fetch(`/api/creators?${params}`);
        if (!response.ok) {
          throw new Error('Failed to fetch creators');
        }

        const data: CreatorsResponse = await response.json();
        let filtered = data.creators ?? [];

        // Client-side filtering for niche and language
        if (nicheFilter) {
          filtered = filtered.filter(c => 
            c.primary_niche?.toLowerCase().includes(nicheFilter.toLowerCase())
          );
        }

        if (languageFilter) {
          filtered = filtered.filter(c =>
            c.languages?.toLowerCase().includes(languageFilter.toLowerCase())
          );
        }

        setCreators(filtered);
        setTotalItems(data.total ?? 0);
        if (data.countries?.length) {
          setCountries(data.countries);
        }
      } catch (err: any) {
        console.error('🔴 [TopInfluencers] Error:', err);
        setError('Unable to load creators. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadCreators();
  }, [countryFilter, nicheFilter, languageFilter, currentPage]);

  const totalPages = Math.ceil(totalItems / limit);

  const handleResetFilters = () => {
    setCountryFilter('');
    setNicheFilter('');
    setLanguageFilter('');
    setCurrentPage(1);
  };

  return (
    <div className="flex-1 bg-white">
      <SEO
        title="Top Travel Influencers & Creators | Waybook"
        description="Discover top travel influencers and creators from around the world. Explore their journeys, get inspired, and connect with the best travel content creators."
        keywords="travel influencers, travel creators, top travel bloggers, travel content creators, wanderlust influencers"
        canonicalUrl="https://www.waybook.me/recommended/top-influencers"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Top Travel Influencers',
          description: 'Discover top travel influencers and creators from around the world',
          url: 'https://www.waybook.me/recommended/top-influencers'
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <FilterSidebar
            countries={countries}
            onCountryChange={(c) => {
              setCountryFilter(c);
              setCurrentPage(1);
            }}
            onNicheChange={(n) => {
              setNicheFilter(n);
              setCurrentPage(1);
            }}
            onLanguageChange={(l) => {
              setLanguageFilter(l);
              setCurrentPage(1);
            }}
            selectedCountry={countryFilter}
            selectedNiche={nicheFilter}
            selectedLanguage={languageFilter}
          />

          {/* Main Content */}
          <main className="flex-1">
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                <h1 className="text-3xl font-bold text-black">Top Travel Influencers</h1>
                <p className="text-sm text-gray-600">
                  {totalItems > 0 ? `${totalItems} creators found` : 'No creators found'}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className="border border-gray-200 bg-white animate-pulse"
                    >
                      <div className="h-64 bg-gray-200" />
                      <div className="p-4 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="border border-gray-200 bg-white p-8 text-center">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              ) : !creators.length ? (
                <div className="border border-gray-200 bg-white p-8 text-center">
                  <p className="text-sm text-gray-500">No creators found for this filter.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {creators.map((creator) => (
                      <InfluencerCard
                        key={creator.id}
                        id={creator.id}
                        name={creator.full_name || creator.brand_name || 'Unknown'}
                        photo={creator.profile_photo_url || null}
                        country={creator.country || null}
                        niche={creator.primary_niche || null}
                        languages={creator.languages || null}
                        followers={creator.total_followers || null}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="bg-black text-waybook-green px-6 py-2 text-sm font-semibold transition-colors hover:bg-gray-900 disabled:opacity-30 disabled:hover:bg-black"
                      >
                        Previous
                      </button>

                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          Page {currentPage} of {totalPages}
                        </span>
                      </div>

                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="bg-black text-waybook-green px-6 py-2 text-sm font-semibold transition-colors hover:bg-gray-900 disabled:opacity-30 disabled:hover:bg-black"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
